import { computed, defineComponent, h, ref } from 'vue'
import { QBtn, QIcon, QInput } from 'quasar'

import { BsComponentRef, bsComponentProps, useBsComponent } from '../useBsComponent'
import { useVm } from '../../../composables/useVm'
import { ComponentState } from '../useBsComponent'
import { BsBtn } from '../index'
import { validInt, validStr } from 'booksprout'
import { quasarHandlers } from '../quasarHandlers'
import { useTemplateRef } from '../useTemplateRef'
import { emitModel } from '../emitModel'

/**
 * Every text field in the apps, plus the variants that are really their own components: a password field
 * with a reveal toggle, a search field with a button in its append slot, and a number field with the
 * plus/minus pair and the min/max clamping that goes with it.
 *
 * The label is rendered above the input by hand rather than handed to QInput, which is why the `label`
 * prop is deleted before the props reach it.
 */

/**
 * `setValue` writes `value` straight onto the instance: it is neither a prop nor a ref, so the write is
 * non-reactive and the only reader is the search button. The class did exactly this behind two
 * `@ts-ignore`s, and its spec pins both halves — including that setting it does not dirty
 * `componentValue`, which reads `$attrs.value`.
 */
type WithInternalValue = { value?: string | number }

/**
 * A `$refs` entry holding a BsInput: `BsComponentRef` plus the members the seven screens that reach into
 * one call. See the note on `BsComponentRef` for why a cast to the component itself no longer typechecks.
 */
export type BsInputRef = BsComponentRef & {
  focus: () => void
  setValue: (v: string | number) => void
  triggerAutoSave: () => void
  resetValidation: () => void
}

export default defineComponent({
  name: 'BsInput',

  /**
   * Declared, and this is not tidiness: an UNDECLARED event whose name is also a DOM event gets bound
   * twice. Vue 3 keeps a parent's `@input` in `$attrs` and puts it on the root element as a native
   * listener as well as treating it as a component event — so `@input="doSearch"` ran once with the
   * string this component emits and again with the raw KeyboardEvent bubbling out of the native input.
   * The second call sent an event object to the API as the search needle, which came back as an error
   * dialog that stole focus from the field mid-typing.
   *
   * `update:modelValue` is here because the composable emits it alongside `input` for `v-model`.
   *
   * Only the events this component emits ITSELF are listed. `blur`, `clear` and `search` are emitted
   * here; anything else a caller binds stays in `$attrs`, which is how `getListeners` forwards it to
   * the QInput inside.
   */
  emits: [ 'input', 'update:modelValue', 'blur', 'clear', 'search' ],

  props: {
    ...bsComponentProps,
    password: { type: Boolean, default: false },
    search: { type: Boolean, default: false },
    hideOptional: { type: Boolean, default: false },
    small: { type: Boolean, default: false },
    srLabel: { type: Boolean, default: false },
    trimAllSpaces: { type: Boolean, default: false },

    icon: { type: String, default: void 0 },
    append: { type: String, default: void 0 },
    clearIcon: { type: String, default: void 0 }
  },

  setup (props, { emit }) {
    // Template refs. A string `ref` in a render function does not populate `$refs` for a
    // component whose logic is in setup() -- see useTemplateRef.
    const { ref: qInput, set: setQInputRef } = useTemplateRef<any>()

    const vm = useVm()
    const bs = useBsComponent({ vm, props, emit })

    const passwordVisible = ref(false)

    const backgroundColor = computed(() => {
      if (bs.hasErrors.value) {
        return 'imp-bs-r-l'
      }

      return vm.$attrs['bg-color']
    })

    const isNumericField = computed(() => vm.$attrs.type === 'number')

    return {
      qInput,
      setQInputRef,
      ...bs,
      passwordVisible,
      backgroundColor,
      isNumericField
    }
  },

  methods: {
    triggerAutoSave () {
      return this.doAutoSave()
    },

    setValue (v: string | number) {
      ;(this as unknown as WithInternalValue).value = v
      if (validStr(v + '') || validInt(v)) {
        this.componentState = ComponentState.Valid
        // setValue is only ever called with a value the caller has already validated (the date-picker
        // click handlers set it inside their valid* guards). The error slot renders `errorMessage`
        // regardless of componentState, so a stale message from an earlier failed pass would keep
        // showing even though the component is now Valid — clear it here as we assert validity.
        this.errorMessage = ''
      }
    },

    focus () {
      ;(this.qInput as QInput).focus()
    },

    resetValidation () {
      (this.qInput as QInput).resetValidation()
      this.reset()
    },

    normalizeNumericValue (val: any) {
      if (val === '' || val === null || val === void 0) {
        return void 0
      }

      const parsedValue = parseInt(val)
      return isNaN(parsedValue) ? void 0 : parsedValue
    },

    ensureValidIntValue (val: any) {
      if (
        !this.isNumericField ||
        val === '' ||
        val === null ||
        val === void 0
      ) {
        return
      }

      const intVal = parseInt(val)
      const min = parseInt(this.$attrs.min)
      const max = parseInt(this.$attrs.max)

      if (
        !validInt(val) || // Not an int
        intVal < min || // OR the entered value is less than min ...
        intVal > max // OR the entered value is more than max
      ) {
        if (intVal < min) {
          emitModel(this, parseInt(this.$attrs.min))
        } else {
          emitModel(this, parseInt(this.$attrs.max))
        }
      }
    },

    /** Renamed from `__renderBeforeSlot`: Vue does not proxy setup/method keys beginning with `_`. */
    renderBeforeSlot () {
      return h('div', { class: 'bs-input__icon' }, [
        h(QIcon, { name: this.icon })
      ])
    },

    renderNumberSlots () {
      const rootValue = this.componentValue
      const value = parseInt(rootValue)
      const valueIsMin = value === parseInt(this.$attrs.min)
      const valueIsMax = value === parseInt(this.$attrs.max)

      return {
        before: () => {
          return h(BsBtn, {
            icon: 'app:minus',
            iconOnly: true,
            primary: valueIsMin,
            secondary: !valueIsMin,
            disable: valueIsMin,
            onClick: () => {
                const value = isNaN(parseInt(rootValue)) ? 0 : parseInt(rootValue) - 1
                if (this.$attrs.min !== void 0 && value < parseInt(this.$attrs.min)) {
                  return
                }

                this.validate(value)
                emitModel(this, value) // Still allow them to change the value as they need to go down / up
                this.doAutoSave()
            },
            ['data-cy']: 'decreaseValue'
          })
        },
        after: () => {
          return h(BsBtn, {
            icon: 'app:plus',
            iconOnly: true,
            primary: valueIsMax,
            secondary: !valueIsMax,
            disable: valueIsMax,
            onClick: () => {
                const value = isNaN(parseInt(rootValue)) ? 1 : parseInt(rootValue) + 1
                if (this.$attrs.max !== void 0 && value > parseInt(this.$attrs.max)) {
                  return
                }

                this.validate(value)
                emitModel(this, value) // Still allow them to change the value as they need to go down / up
                this.doAutoSave()
            },
            ['data-cy']: 'increaseValue'
          })
        }
      }
    },

    renderSearchSlot () {
      return {
        append: () => {
          return h(QBtn, {
            class: ['bs-search-btn', { 'bs-search-btn__focused': this.componentValue }],
            // The internal `value`, not the bound one — see `WithInternalValue`. Pinned by the spec.
            onClick: () => this.$emit('search', (this as unknown as WithInternalValue).value)
          }, {
            default: () => h(QIcon, {
              name: 'app:magnifier-dense',
              color: this.componentValue ? 'white' : 'bs-grey-m',
              size: '.75rem'
            })
          })
        }
      }
    },

    renderScopedSlots () {
      let slots = {}

      if (this.password) {
        slots = {
          ...slots,
          append: () => h(QIcon, {
            class: 'cursor-pointer',
            name: this.passwordVisible ? 'app:password-visible' : 'app:password-hidden',
            onClick: () => this.passwordVisible = !this.passwordVisible
          })
        }
      }

      if (this.append) {
        slots = {
          ...slots,
          append: () => h('span', { class: 'font-size-13 text-primary' }, this.append)
        }
      }

      if (this.icon) {
        slots = {
          ...slots,
          before: () => this.renderBeforeSlot()
        }
      }

      if (this.isNumericField) {
        slots = {
          ...slots,
          ...this.renderNumberSlots()
        }
      }

      if (this.search) {
        slots = {
          ...slots,
          ...this.renderSearchSlot()
        }
      }

      return slots
    },

    renderLabelSlot () {
      /**
       * `tkey` used to reach here by being written back onto `$attrs` in getRenderProps. `$attrs` is
       * readonly in Vue 3, so the translation is resolved here instead -- otherwise a tkey-only field
       * renders no label at all.
       */
      const label = this.$attrs.label ?? (this.tkey ? this.$tc(this.tkey) : void 0)

      if (label === void 0 && this.$slots.actions === void 0) {
        return void 0
      }

      return h('div', {
        class: ['bs-input-container__label flex justify-between items-center', {
          'sr-only': this.srLabel
        }],
        ['aria-hidden']: true // just needed visually
      }, [
        h('div', {
          class: 'cursor-pointer',
          onClick: this.focus
        }, [
          label,
          this.required || this.hideOptional
            ? void 0
            : h('span', { class: 'bs-input__optional' }, ' (optional)')
        ]),
        this.$slots.actions && h('div', this.slot(this, 'actions'))
      ])
    }
  },

  render () {
    if (this.isNumericField) {
      this.ensureValidIntValue(this.componentValue)
    }

    const value = this.componentValue
    const props = this.getRenderProps({
      stackLabel: true,
      value,
      /**
       * `true`, where Vue 2 had `false` and still got a bottom row.
       *
       * QField renders its bottom when `bottomSlots || hint || rules || counter || error !== null`,
       * and on Vue 2 `getAttributes` passed `error: false` -- not null, so the row was always there.
       * `getAttributes` now passes `null` when clear, because Vue 3 renders a `false` attribute as
       * the string "false" on the components that have no `error` prop of their own (QBtn, QRating).
       * That took the bottom row away from the two field components that were relying on it, and
       * with it the auto-save "Your changes were saved!" hint -- which is what the e2e suite waits
       * for after every autoSave field. Asking for the row explicitly is what Vue 2 did in effect.
       */
      bottomSlots: true,
      noErrorIcon: true,
      outlined: this.hasErrors,
      bgColor: this.backgroundColor,
      type: this.password ? this.passwordVisible ? 'text' : 'password' : this.$attrs.type || 'text',
      clearable: this.search,
      clearIcon: this.clearIcon ? this.clearIcon : 'app:close-fat',
      readonly: this.$attrs.readonly
    })

    // We handle the label ourselves by putting it above the input.
    // Remove the prop
    delete props.label

    const scopedSlots = {
      ...this.renderScopedSlots()
    }

    return h('div', {
      class: 'bs-input-container',
      /**
       * The id used to come from `$vnode.data.model.expression` -- the compiler's record of what
       * `v-model` was bound to, which Vue 3 does not keep. `dataModel` is the explicit replacement
       * (see bsComponentProps), so a caller whose tests select on this passes it.
       */
      id: this.dataModel
    }, [
      this.renderLabelSlot(),
      h(QInput, {
        ref: this.setQInputRef,
        class: [this.$attrs.type === 'textarea' ? 'bs-textarea' : 'bs-input', this.getClasses({
          'bs-input__search': this.search,
          // `componentValue` throughout: `v-model` sends `modelValue`, and the old name is undefined.
          'bs-input__search--not-empty': this.search && this.componentValue,
          'bs-input__icon': this.icon,
          'bs-input__small': this.small,
          'bs-input__number': this.isNumericField
        })],
        ...props,
        ...quasarHandlers(this.getListeners({
          blur: () => {
            // `componentValue`, not `$attrs.value`: a `v-model` caller sends `modelValue`, so this
            // validated `undefined` on every blur and marked a filled field invalid.
            this.validate(this.componentValue)
            this.$emit('blur')
          },
          input: (val: any) => {
            if (this.search && (val === null || val === void 0)) {
              val = ''
            }
            if (this.trimAllSpaces) {
              val = val.replace(' ', '')
            }
            emitModel(this, this.isNumericField ? this.normalizeNumericValue(val) : val)
          },
          keyup: (e: any) => {
            const val = e.target.value
            this.ensureValidIntValue(val)
          },
          clear: () => {
            this.$emit('clear', this.isNumericField ? parseInt(this.$attrs.min) : '')
          }
        })),
        ...this.getAttributes({
          ['data-cy']: this.$attrs['data-cy'],
          ['data-hj-allow']: this.$attrs['data-hj-allow'],
          inputmode: this.isNumericField ? 'numeric' : this.$attrs.inputmode,
          pattern: this.isNumericField ? '[0-9]+' : this.$attrs.pattern
        })
      }, {
        ...this.getSlots(scopedSlots),
        default: () => this.slot(this, 'default')
      })
    ])
  }
})
